import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { UAInsightsBootSkeleton } from '../components/UAInsightsBootSkeleton/UAInsightsBootSkeleton'
import { UAInsightsErrorState } from '../components/UAInsightsErrorState/UAInsightsErrorState'
import type { ObjectId, UAInsightsDialogType } from '../types'
import { CompareTab, GENRE_FILTER_QUERY_PARAM, MONTH_QUERY_PARAM, NETWORK_QUERY_PARAM, useUAInsightsDialogParams } from './useUAInsightsDialogParams'
import { useUAInsightsMeta } from './useUAInsightsMeta'

export type { CompareTab } from './useUAInsightsDialogParams'

type DialogParams = ReturnType<typeof useUAInsightsDialogParams>['params']

/**
 * Navigation *actions* — the high-level `openX` verbs, the scope setters and
 * the close/back controls. Every argument is an opaque API id.
 *
 * Every function here keeps a stable identity for the lifetime of the
 * provider. That matters because most of the tree (tables, chart rows, tag
 * cells) consumes actions only: without stability they would re-render on
 * every URL change, since the underlying mutators all close over the current
 * `searchParams`. Read this via {@link useUAInsightsActions} rather than
 * {@link useUAInsightsNavigation} wherever a component needs no params.
 */
export interface UAInsightsActions {
  openTag: (tagId: ObjectId) => void
  openTagForGame: (tagId: ObjectId, gameId: ObjectId) => void
  openAd: (creativeId: ObjectId) => void
  openGame: (gameId: ObjectId) => void
  openMotivation: (motivationId: ObjectId) => void
  openBlindSpot: (motivationId: ObjectId) => void
  openAlignment: (motivationId: ObjectId) => void
  /**
   * Open the cross-entity drilldown for a motivation classified as a Shared
   * Motivation in the comparison dialog. The comparison context (target game
   * id or target genre id) is read from the existing `compareGameId` /
   * `compareGenreId` URL params already set by the parent comparison dialog,
   * so callers only need to pass the motivation id.
   */
  openSharedMotivation: (motivationId: ObjectId) => void
  /**
   * Open the cross-entity drilldown for a motivation classified as a
   * Diverging Motivation in the comparison dialog. Same comparison context
   * source as `openSharedMotivation`.
   */
  openDivergingMotivation: (motivationId: ObjectId) => void
  openGenreTags: (genreId: ObjectId) => void
  openTagGames: (tagId: ObjectId) => void
  openDimensionTags: (dimensionId: ObjectId) => void
  openAllCreatives: () => void
  openAllGames: () => void
  openAllTags: () => void
  openCompareGame: () => void
  openCompareGameResult: (targetGameId: ObjectId) => void
  openCompareGenreResult: (targetGenreId: ObjectId) => void
  close: () => void
  goBack: () => void
  setNetwork: (networkId: ObjectId | null) => void
  setMonth: (month: string | null) => void
  setGenreFilter: (genreId: ObjectId | null) => void
  setCompareTab: (tab: CompareTab | null) => void
  /** Open the in-page Motivation detail view. */
  openMotivationView: (motivationId: ObjectId) => void
  /** Open the in-page Story Pattern detail view, optionally lensed through a motivation. */
  openPatternView: (patternId: ObjectId, lensMotivationId?: ObjectId | null) => void
  /** Return from a detail view to the dashboard overview. */
  backToOverview: () => void
}

/**
 * The active scope filters. Changes only when the user picks a different
 * network / month / genre or switches compare tab — not when a dialog opens.
 */
export interface UAInsightsScope {
  networkId: ObjectId | null
  month: string | null
  /** Currently selected dashboard-wide genre filter (genre id), or null for "All Genres". */
  genreFilter: ObjectId | null
  compareTab: CompareTab | null
}

/** The full URL params snapshot. Changes on any UA Insights URL change. */
export interface UAInsightsParams {
  params: DialogParams
  canGoBack: boolean
}

/**
 * Everything the navigation layer exposes. Retained so dialogs — which need
 * the params snapshot anyway — can keep reading a single object.
 */
export interface UAInsightsNavigation extends UAInsightsActions, UAInsightsScope, UAInsightsParams {}

/**
 * Build the query string carried onto the Compare page so it opens with the
 * same network/month scope the user was viewing on the game page. The active
 * compare tab defaults to Overview, so it is intentionally not seeded here.
 */
const comparePageSearch = (params: { networkId: string | null; month: string | null }): string => {
  const qs = new URLSearchParams()
  if (params.networkId) qs.set(NETWORK_QUERY_PARAM, params.networkId)
  if (params.month) qs.set(MONTH_QUERY_PARAM, params.month)
  const search = qs.toString()
  return search ? `?${search}` : ''
}

const UAInsightsActionsContext = createContext<UAInsightsActions | null>(null)
const UAInsightsScopeContext = createContext<UAInsightsScope | null>(null)
const UAInsightsParamsContext = createContext<UAInsightsParams | null>(null)

interface UAInsightsNavigationProviderProps {
  children: ReactNode
}

export const UAInsightsNavigationProvider: FC<UAInsightsNavigationProviderProps> = ({ children }) => {
  const { isLoading, isError, hasMeta, refetch } = useUAInsightsMeta()
  if (isLoading) {
    return <UAInsightsBootSkeleton />
  }
  // `/meta` is the prerequisite for everything below: the networks, months and
  // genres that every scope default is derived from all come from it. Rendering
  // the feature anyway would boot it with an empty scope, and the retry buttons
  // on the surfaces inside would each retry their own request rather than the
  // one that actually failed. So the boot failure is surfaced here instead.
  if (isError && !hasMeta) {
    return <UAInsightsErrorState onRetry={refetch} />
  }
  return <UAInsightsNavigationProviderReady>{children}</UAInsightsNavigationProviderReady>
}

const UAInsightsNavigationProviderReady: FC<UAInsightsNavigationProviderProps> = ({ children }) => {
  const dialogParams = useUAInsightsDialogParams()
  const { params, canGoBack } = dialogParams
  const navigate = useNavigate()
  // Source game id of the active comparison (the game currently on the UA
  // Insights game page the Compare action was triggered from). Read here so
  // the compare-result navigators can build the page path
  // `/ua-insights/game/:gameId/compare/...`.
  const { gameId: sourceGameIdParam } = useParams<{ gameId: string }>()

  // Actions are built once and read their dependencies through this ref, which
  // is why they can keep a stable identity while the underlying mutators are
  // rebuilt on every URL change. Assigned during render so an action invoked
  // in the same commit still sees current values; actions only ever run from
  // event handlers, never during render, so reading it is safe.
  const latest = useRef({ dialogParams, navigate, sourceGameIdParam })
  latest.current = { dialogParams, navigate, sourceGameIdParam }

  // Provider only renders this subtree once `/meta` has loaded, so the
  // defaults and lookups are populated when the seeding effect below runs.
  const { defaultNetworkId, defaultMonth, networkById, genreById, months } = useUAInsightsMeta()

  // Seed the default network + month on first render so every hook in the tree
  // sees a stable initial selection, and replace a network, month or genre the
  // current `/meta` doesn't know (e.g. a stale bookmark) instead of sending it
  // to every endpoint as a 404. Defaults come from `/meta`, so new networks
  // and months surface automatically.
  useEffect(() => {
    const url = new URL(window.location.href)
    const updates: Array<[string, string | null]> = []
    if (params.networkId ? !networkById(params.networkId) : defaultNetworkId) updates.push([NETWORK_QUERY_PARAM, defaultNetworkId])
    if (params.month ? !months.includes(params.month) : defaultMonth) updates.push([MONTH_QUERY_PARAM, defaultMonth])
    if (params.genreFilter && !genreById(params.genreFilter)) updates.push([GENRE_FILTER_QUERY_PARAM, null])
    // Apply every change together. The individual setters each derive a
    // fresh `URLSearchParams` from the captured snapshot, so calling them
    // sequentially in the same render would race and only the last one would
    // actually persist.
    if (updates.length) {
      updates.forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value)
        else url.searchParams.delete(key)
      })
      navigate({ search: url.searchParams.toString() }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actions = useMemo<UAInsightsActions>(() => {
    const openDialog = (...args: Parameters<typeof dialogParams.openDialog>) => latest.current.dialogParams.openDialog(...args)
    const openMotivationDialog = (dialog: UAInsightsDialogType, motivationId: ObjectId) => openDialog(dialog, { motivationId })

    return {
      openTag: (tagId) => openDialog('tag', { tagId }),
      openTagForGame: (tagId, gameId) => openDialog('tag', { tagId, gameId }),
      openAd: (creativeId) => openDialog('ad', { creativeId }),
      openGame: (gameId) => {
        const current = latest.current.dialogParams.params
        const qs = new URLSearchParams()
        if (current.networkId) qs.set(NETWORK_QUERY_PARAM, current.networkId)
        if (current.month) qs.set(MONTH_QUERY_PARAM, current.month)
        if (current.genreFilter) qs.set(GENRE_FILTER_QUERY_PARAM, current.genreFilter)
        const search = qs.toString() ? `?${qs.toString()}` : ''
        latest.current.navigate(`/ua-insights/game/${gameId}${search}`)
      },
      openMotivation: (motivationId) => openMotivationDialog('motivation', motivationId),
      openBlindSpot: (motivationId) => openMotivationDialog('blind-spot', motivationId),
      openAlignment: (motivationId) => openMotivationDialog('alignment', motivationId),
      openSharedMotivation: (motivationId) => openMotivationDialog('shared-motivation', motivationId),
      openDivergingMotivation: (motivationId) => openMotivationDialog('diverging-motivation', motivationId),
      openGenreTags: (genreId) => openDialog('genre-tags', { genreId }),
      openTagGames: (tagId) => openDialog('tag-games', { tagId }),
      openDimensionTags: (dimensionId) => openDialog('dimension-tags', { dimensionId }),
      openAllCreatives: () => openDialog('all-creatives'),
      openAllGames: () => openDialog('all-games'),
      openAllTags: () => openDialog('all-tags'),
      openCompareGame: () => openDialog('compare-game'),
      // Comparison results are full pages (not dialogs). Preserve the active
      // network/month scope so the page opens with the same filters the user
      // was viewing on the game page.
      openCompareGameResult: (targetGameId) => {
        const { dialogParams: current, navigate: go, sourceGameIdParam: sourceId } = latest.current
        go(`/ua-insights/game/${sourceId}/compare/game/${targetGameId}${comparePageSearch(current.params)}`)
      },
      openCompareGenreResult: (targetGenreId) => {
        const { dialogParams: current, navigate: go, sourceGameIdParam: sourceId } = latest.current
        go(`/ua-insights/game/${sourceId}/compare/genre/${targetGenreId}${comparePageSearch(current.params)}`)
      },
      close: () => latest.current.dialogParams.closeDialog(),
      goBack: () => latest.current.dialogParams.goBack(),
      setNetwork: (networkId) => latest.current.dialogParams.setNetwork(networkId),
      setMonth: (month) => latest.current.dialogParams.setMonth(month),
      setGenreFilter: (genreId) => latest.current.dialogParams.setGenreFilter(genreId),
      setCompareTab: (tab) => latest.current.dialogParams.setCompareTab(tab),
      openMotivationView: (motivationId) => latest.current.dialogParams.openMotivationView(motivationId),
      openPatternView: (patternId, lensMotivationId) => latest.current.dialogParams.openPatternView(patternId, lensMotivationId),
      backToOverview: () => latest.current.dialogParams.backToOverview(),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Unknown values read as unset until the seeding effect above rewrites the
  // URL, so the first render never queries with an id the API would reject.
  const scope = useMemo<UAInsightsScope>(
    () => ({
      networkId: params.networkId && networkById(params.networkId) ? params.networkId : null,
      month: params.month && months.includes(params.month) ? params.month : null,
      genreFilter: params.genreFilter && genreById(params.genreFilter) ? params.genreFilter : null,
      compareTab: params.compareTab,
    }),
    [params.networkId, params.month, params.genreFilter, params.compareTab, networkById, genreById, months]
  )

  const paramsValue = useMemo<UAInsightsParams>(() => ({ params, canGoBack }), [params, canGoBack])

  return (
    <UAInsightsActionsContext.Provider value={actions}>
      <UAInsightsScopeContext.Provider value={scope}>
        <UAInsightsParamsContext.Provider value={paramsValue}>{children}</UAInsightsParamsContext.Provider>
      </UAInsightsScopeContext.Provider>
    </UAInsightsActionsContext.Provider>
  )
}

const useRequiredContext = <T,>(value: T | null, hookName: string): T => {
  if (!value) {
    throw new Error(`${hookName} must be used within a UAInsightsNavigationProvider`)
  }
  return value
}

/**
 * Navigation actions only. Prefer this over {@link useUAInsightsNavigation} —
 * the returned object never changes identity, so consuming it does not
 * re-render the component when the URL changes.
 */
export const useUAInsightsActions = (): UAInsightsActions => useRequiredContext(useContext(UAInsightsActionsContext), 'useUAInsightsActions')

/** The active network / month / genre / compare-tab selection. */
export const useUAInsightsScope = (): UAInsightsScope => useRequiredContext(useContext(UAInsightsScopeContext), 'useUAInsightsScope')

/** The full URL params snapshot. Only dialogs and pages should need this. */
export const useUAInsightsParams = (): UAInsightsParams => useRequiredContext(useContext(UAInsightsParamsContext), 'useUAInsightsParams')

/**
 * Everything at once. Subscribes to all three contexts, so it re-renders on
 * any URL change — appropriate for dialogs and pages that read `params`, but
 * prefer {@link useUAInsightsActions} or {@link useUAInsightsScope} elsewhere.
 */
export const useUAInsightsNavigation = (): UAInsightsNavigation => {
  const actions = useUAInsightsActions()
  const scope = useUAInsightsScope()
  const paramsValue = useUAInsightsParams()
  return useMemo(() => ({ ...actions, ...scope, ...paramsValue }), [actions, scope, paramsValue])
}
